/**
 * Field rendering for `CrmCreateForm`: sections (flat or accordion) and the
 * three field kinds, every one full width in a single column. Stateless —
 * the form owns values, validation and the callbacks passed in here.
 */

import type { FocusEvent, ReactNode } from 'react';
import { memo, useCallback } from 'react';
import type { Key } from 'react-aria-components';

import {
  Accordion,
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import type { FormContextValue } from '@/lib/base/forms/form-context';
import { HintText } from '@/lib/base/forms/hint-text';
import { DraftParentBanner } from '@/lib/crm/components/DraftParentBanner';
import { CrmAssociationField } from '@/lib/crm/components/fields/CrmAssociationField';
import { CrmPropertyField } from '@/lib/crm/components/fields/CrmPropertyField';
import { LabelWithIndicator } from '@/lib/crm/components/fields/LabelWithIndicator';
import type {
  DraftEntity,
  DraftParentContext,
  DraftParentInfo,
} from '@/lib/crm/core/draftEntities';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

import { fieldKey, isFieldVisible } from './fieldVisibility';
import type {
  CrmCreateCustomField,
  CrmCreateField,
  CrmCreatePropertyField,
  CrmCreateSection,
} from './types';

// Section heading scale. Mirrors the CRM record-view section card
// (lib/crm/record/components/SectionCard.tsx) — the house pattern for a card
// section title. SectionCard's `2xl:text-heading-3` step is deliberately
// dropped: the panel is width-capped whatever the viewport, so escalating the
// heading on wide screens would oversize it relative to the fields beneath.
const SECTION_TITLE_CLASSES =
  'text-sm lg:text-base font-semibold text-[var(--color-text-primary)]';

/** Fields stack in one full-width column — no side-by-side pairs. */
const FIELD_COLUMN_CLASSES = 'flex w-full flex-col gap-4';

/**
 * Ignore blur events that stay inside the field (e.g. input → trailing
 * button); only a real departure marks the field touched.
 */
function useBlurWithin(onBlur: () => void) {
  return useCallback(
    (e: FocusEvent) => {
      if (e.currentTarget.contains(e.relatedTarget as Node)) {
        return;
      }
      onBlur();
    },
    [onBlur]
  );
}

// ─── Property ────────────────────────────────────────────────────────────────

interface PropertyRowProps {
  field: CrmCreatePropertyField;
  schema: PropertyTypeSchema | undefined;
  value: unknown;
  getFieldProps: FormContextValue['getFieldProps'];
  onChange: (name: string, value: unknown) => void;
  disabled: boolean;
}

const PropertyRow = memo(function PropertyRow({
  field,
  schema,
  value,
  getFieldProps,
  onChange,
  disabled,
}: PropertyRowProps) {
  const fieldProps = getFieldProps(field.property);
  const handleChange = useCallback(
    (next: unknown) => onChange(field.property, next),
    [onChange, field.property]
  );
  const handleBlur = useBlurWithin(fieldProps.onBlur);

  if (!schema) {
    return (
      <div className='p-3 bg-utility-error-50 border border-utility-error-200 rounded-lg'>
        <p className='text-sm text-utility-error-700'>
          Property schema not found:{' '}
          <code className='font-mono'>{field.property}</code>
        </p>
      </div>
    );
  }

  return (
    <div data-form-field={field.property} onBlurCapture={handleBlur}>
      <CrmPropertyField
        propertyType={schema}
        label={field.label}
        isRequired={field.required}
        hint={field.helpText}
        value={value as string | number | object | null | undefined}
        onChange={handleChange}
        disabled={disabled}
        error={fieldProps.showError ? fieldProps.error : undefined}
      />
    </div>
  );
});

// ─── Custom ──────────────────────────────────────────────────────────────────

interface CustomRowProps {
  field: CrmCreateCustomField;
  value: unknown;
  getFieldProps: FormContextValue['getFieldProps'];
  onChange: (name: string, value: unknown) => void;
  disabled: boolean;
}

const CustomRow = memo(function CustomRow({
  field,
  value,
  getFieldProps,
  onChange,
  disabled,
}: CustomRowProps) {
  const fieldProps = getFieldProps(field.property);
  const handleChange = useCallback(
    (next: unknown) => onChange(field.property, next),
    [onChange, field.property]
  );
  const handleBlur = useBlurWithin(fieldProps.onBlur);
  const error = fieldProps.showError ? fieldProps.error : undefined;

  return (
    <div
      data-form-field={field.property}
      onBlurCapture={handleBlur}
      className='flex flex-col gap-1.5'
    >
      {field.label !== '' && (
        <LabelWithIndicator label={field.label} isRequired={field.required} />
      )}
      {field.render(value, handleChange, disabled)}
      {error && <span className='text-sm text-utility-error-600'>{error}</span>}
      {!error && field.helpText && <HintText>{field.helpText}</HintText>}
    </div>
  );
});

// ─── Association ─────────────────────────────────────────────────────────────

export interface AssociationRowContext {
  orgId: number;
  disabled: boolean;
  stageTypeNames: Set<string>;
  /** Bumped on every "& add another" reset so pickers remount. */
  formResetKey: number;
  draftParentContext: DraftParentContext | undefined;
  /** Lower-cased label of the parent type, for the draft-parent banner. */
  parentTypeLabel: string;
  draftParentInfo: DraftParentInfo;
  onCreateDraft: (draft: DraftEntity) => void;
  /** Per-association change handlers, stable per association type name. */
  associationCallbacks: Record<
    string,
    (value: string | string[] | null) => void
  >;
}

function AssociationRow({
  associationType,
  value,
  ctx,
}: {
  associationType: AssociationType;
  value: unknown;
  ctx: AssociationRowContext;
}) {
  const { draftParentContext } = ctx;
  if (
    draftParentContext &&
    associationType.association_type_name ===
      draftParentContext.reverseAssociationTypeName
  ) {
    return (
      <DraftParentBanner
        label={associationType.label}
        parentTypeLabel={ctx.parentTypeLabel}
        displayName={draftParentContext.displayName}
      />
    );
  }
  return (
    <CrmAssociationField
      key={`${associationType.association_type_name}-${ctx.formResetKey}`}
      associationType={associationType}
      value={value as string | string[] | null | undefined}
      onChange={ctx.associationCallbacks[associationType.association_type_name]}
      onCreateDraft={ctx.onCreateDraft}
      orgId={ctx.orgId}
      disabled={ctx.disabled}
      stageTypeNames={ctx.stageTypeNames}
      allowOverwrite
      hideCreateOption={!!draftParentContext}
      draftParentInfo={ctx.draftParentInfo}
    />
  );
}

// ─── Sections ────────────────────────────────────────────────────────────────

export interface CrmCreateFieldsProps {
  sections: CrmCreateSection[];
  sectionStyle: 'flat' | 'accordion';
  formData: Record<string, unknown>;
  propertySchemas: Record<string, PropertyTypeSchema>;
  /**
   * Association types the form actually renders, keyed by name — already
   * narrowed by the domain-kind filter and to those whose schema has loaded.
   */
  associationTypesByName: ReadonlyMap<string, AssociationType>;
  getFieldProps: FormContextValue['getFieldProps'];
  onPropertyChange: (name: string, value: unknown) => void;
  onCustomChange: (name: string, value: unknown) => void;
  disabled: boolean;
  associationCtx: AssociationRowContext;
  /** Accordion state; ignored for the flat style. */
  expandedKeys: Set<Key>;
  onExpandedChange: (keys: Set<Key>) => void;
}

function renderField(
  field: CrmCreateField,
  props: CrmCreateFieldsProps
): ReactNode {
  switch (field.kind) {
    case 'property':
      return (
        <PropertyRow
          key={fieldKey(field)}
          field={field}
          schema={props.propertySchemas[field.property]}
          value={props.formData[field.property]}
          getFieldProps={props.getFieldProps}
          onChange={props.onPropertyChange}
          disabled={props.disabled}
        />
      );
    case 'custom':
      return (
        <CustomRow
          key={fieldKey(field)}
          field={field}
          value={props.formData[field.property]}
          getFieldProps={props.getFieldProps}
          onChange={props.onCustomChange}
          disabled={props.disabled}
        />
      );
    case 'association': {
      const associationType = props.associationTypesByName.get(
        field.associationType
      );
      if (!associationType) {
        return null;
      }
      return (
        <AssociationRow
          key={fieldKey(field)}
          associationType={associationType}
          value={props.formData[field.associationType]}
          ctx={props.associationCtx}
        />
      );
    }
  }
}

function SectionHeading({ section }: { section: CrmCreateSection }) {
  if (!section.title) {
    return null;
  }
  return (
    <div>
      <h3 className={SECTION_TITLE_CLASSES}>{section.title}</h3>
      {section.description && (
        <p className='text-body-small text-[var(--color-text-secondary)] mt-1'>
          {section.description}
        </p>
      )}
    </div>
  );
}

export function CrmCreateFields(props: CrmCreateFieldsProps) {
  const { sections, sectionStyle, formData, associationTypesByName } = props;

  const renderSectionFields = (section: CrmCreateSection) => (
    <div className={FIELD_COLUMN_CLASSES}>
      {section.fields
        .filter(field =>
          isFieldVisible(field, formData, associationTypesByName)
        )
        .map(field => renderField(field, props))}
    </div>
  );

  if (sectionStyle === 'accordion') {
    return (
      <Accordion
        className='flex flex-col gap-3'
        expandedKeys={props.expandedKeys}
        onExpandedChange={props.onExpandedChange}
      >
        {sections.map(section => (
          <AccordionItem
            key={section.id}
            id={section.id}
            className='rounded-xl border border-[var(--color-border-secondary)] data-[expanded]:border-[var(--color-brand-600)]'
          >
            <AccordionTrigger className='flex w-full items-center justify-between gap-3 p-4 text-left'>
              <SectionHeading section={section} />
              <AccordionChevron />
            </AccordionTrigger>
            <AccordionPanel className='px-4 pb-4'>
              {renderSectionFields(section)}
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    );
  }

  return (
    <div className='flex flex-col gap-6'>
      {sections.map(section => (
        <div key={section.id} className='flex flex-col gap-4'>
          <SectionHeading section={section} />
          {renderSectionFields(section)}
        </div>
      ))}
    </div>
  );
}
